import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ORDER_BY_DIRECTION_NUMERIC,
  SORT_FHIR_APPOINTMENT_BY,
} from 'utilities/interfaces';
import {
  useGetAppointmentsByPatientCodexIdLazyQuery,
  useGetAppointmentsByPatientIdLazyQuery,
} from 'graphql/generated/remote-schema-hasura';
import { useAuth } from 'auth/context/AuthContext';
import { Roles } from '../../firebase/interfaces';
import { IAppointmentsFilter } from '../../app/my-patients/pages/patient/tabs/appointmets/useGetPatientAppointments';
import debounce from 'lodash/debounce';
import { AppointmentStatus } from '../../app/my-appointments/interfaces';

export type PreviousAppointmentSummaryType = {
  id: string;
  date: string;
};

const initialFilter = {
  limit: 20,
  sort: SORT_FHIR_APPOINTMENT_BY.DATE,
  sortDirection: ORDER_BY_DIRECTION_NUMERIC.DESC,
  offset: 0,
  isSummaryCompleted: true,
};

export const usePreviousAppointmentSummaries = (patientCodexId: string) => {
  const { user } = useAuth();
  const isProvider = user?.role === Roles.PROVIDER;

  const [filter, setFilter] = useState<IAppointmentsFilter>(() => ({
    ...initialFilter,
    patientCodexId,
  }));

  const [appointments, setAppointments] = useState<
    PreviousAppointmentSummaryType[]
  >([]);

  const [total, setTotal] = useState(0);

  const variables = useMemo(
    () => ({
      limit: +filter.limit,
      offset: +filter.offset,
      sort: filter.sort,
      sortDirection: +filter.sortDirection,
      patientCodexId: filter.patientCodexId,
      isSummaryCompleted: Boolean(filter.isSummaryCompleted) || null,
      status: ['completed'],
    }),
    [filter],
  );

  const [getForPatient, { loading: loadingPatient }] =
    useGetAppointmentsByPatientCodexIdLazyQuery({
      fetchPolicy: 'cache-and-network',
      onCompleted: (data) => {
        const items =
          data.getFHIRAppointmentByRequestPatientCodexId.appointment || [];

        const mapped = items.map((appointment) => ({
          id: appointment.appointmentCodexId,
          date: appointment.start || '',
        }));

        setTotal(data.getFHIRAppointmentByRequestPatientCodexId.total);

        const currentOffset = variables.offset;
        setAppointments((prev) =>
          currentOffset ? [...prev, ...mapped] : mapped,
        );
      },
    });

  const [getForProvider, { loading: loadingProvider }] =
    useGetAppointmentsByPatientIdLazyQuery({
      fetchPolicy: 'cache-and-network',
      onCompleted: (data) => {
        const items =
          data.getFHIRPatientAppointmentsByProvider.appointment || [];

        const mapped = items.map((appointment) => ({
          id: appointment.appointmentCodexId,
          date: appointment.start || '',
        }));

        setTotal(data.getFHIRPatientAppointmentsByProvider.total);

        const currentOffset = variables.offset;
        setAppointments((prev) =>
          currentOffset ? [...prev, ...mapped] : mapped,
        );
      },
    });

  const loading = loadingPatient || loadingProvider;

  const hasMore = total > +filter.offset + +filter.limit;

  const loadingRef = useRef(loading);
  const hasMoreRef = useRef(hasMore);

  useEffect(() => {
    loadingRef.current = loading;
    hasMoreRef.current = hasMore;
  }, [loading, hasMore]);

  const loadMore = useMemo(
    () =>
      debounce(() => {
        if (loadingRef.current || !hasMoreRef.current) return;

        setFilter((prev) => ({
          ...prev,
          offset: +prev.offset + +prev.limit,
        }));
      }, 300),
    [],
  );

  useEffect(() => () => loadMore.cancel(), [loadMore]);

  useEffect(() => {
    if (!patientCodexId) return;

    setFilter((prev) => ({
      ...prev,
      patientCodexId,
    }));
  }, [patientCodexId]);

  useEffect(() => {
    if (!user?.role || !variables.patientCodexId) return;

    if (isProvider) {
      getForProvider({
        variables: {
          ...variables,
          appointmentStatus: [AppointmentStatus.FULFILLED],
        },
      });
    } else {
      getForPatient({
        variables: {
          ...variables,
          appointmentStatus: [AppointmentStatus.FULFILLED],
        },
      });
    }
  }, [
    user?.role,
    isProvider,
    variables,
    getForProvider,
    getForPatient,
    patientCodexId,
  ]);

  return {
    appointments,
    loading,
    hasMore,
    filter,
    setFilter,
    total,
    loadMore,
  };
};
